/**
 * Taslak senkronizasyonu (PDF §06, §13).
 *
 *  1. Taslak yerelde oluşur (kamera açılmadan). Her değişiklik kısa gecikmeyle ve uygulama
 *     arka plana geçerken kaydedilir.
 *  2. Bağlantı varsa: sunucu adayı oluşturulur (aynı client_draft_id ile tekrar güvenli),
 *     görseller SHA-256 ile yüklenir. Sunucu "dosya hazır" demeden gönderim yapılmaz.
 *  3. Kullanıcı alanları kontrol eder → PATCH (sürüm kontrollü) → submit (kayıtlı Idempotency-Key).
 *  4. Hata/kesinti: artan beklemeyle yeniden deneme. "Kaydedildi" yalnızca POSTED'da.
 */
import * as ep from '../api/endpoints';
import type { Candidate } from '../api/types';
import { isoToTR } from '../lib/dates';
import { ApiError, NetworkError } from '../lib/errors';
import { parseUserAmount } from '../lib/money';
import { sha256Hex, uuid } from '../lib/uuid';
import { draftStore } from './store';
import { emptyFields, type Draft, type DraftFields, type DraftImage, type LocalState } from './types';

export const MAX_IMAGES = 5;
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];

export async function newDraft(userId: string): Promise<Draft> {
  const d: Draft = {
    id: uuid(), userId, state: 'LOCAL_DRAFT', fields: emptyFields(), images: [],
    candidateId: null, candidateVersion: null, createIdempotencyKey: uuid(), submitIdempotencyKey: uuid(),
    receiptId: null, lastError: null, createdAt: Date.now(), updatedAt: Date.now(), attempts: 0, nextAttemptAt: 0,
  };
  await (await draftStore()).put(d);
  return d;
}

export async function addImage(draft: Draft, blob: Blob, name: string): Promise<Draft> {
  if (draft.images.length >= MAX_IMAGES) throw new Error(`Bir fiş için en fazla ${MAX_IMAGES} görsel eklenebilir.`);
  const mime = blob.type || guessMime(name);
  if (!ACCEPTED.includes(mime)) throw new Error('Desteklenmeyen dosya türü. JPEG, PNG veya HEIC seçin.');
  if (blob.size > MAX_IMAGE_BYTES) throw new Error('Görsel 15 MB sınırını aşıyor.');
  const img: DraftImage = { id: uuid(), name, mime, size: blob.size, sha256: await sha256Hex(blob), uploadedFileId: null };
  const store = await draftStore();
  await store.putImage(draft.id, img.id, blob); // önce kalıcı kopya, sonra taslak kaydı
  const next = { ...draft, images: [...draft.images, img], state: 'LOCAL_DRAFT' as LocalState, lastError: null };
  await store.put(next);
  return next;
}

export async function saveFields(draft: Draft, fields: Partial<DraftFields>): Promise<Draft> {
  const next = { ...draft, fields: { ...draft.fields, ...fields } };
  await (await draftStore()).put(next);
  return next;
}

/** Sunucu adayını oluşturur ve bekleyen görselleri yükler. Çevrimdışında taslak korunur. */
export async function uploadDraft(draft: Draft): Promise<Draft> {
  const store = await draftStore();
  let d: Draft = { ...draft, state: 'UPLOADING', lastError: null };
  await store.put(d);
  try {
    if (!d.candidateId) {
      const c = await ep.createCandidate(d.id, {}, d.createIdempotencyKey);
      d = { ...d, candidateId: c.id, candidateVersion: c.version };
      await store.put(d);
    }
    for (const img of d.images) {
      if (img.uploadedFileId) continue;
      const blob = await store.getImage(d.id, img.id);
      if (!blob) throw new Error('Taslak görseli cihazda bulunamadı; lütfen görseli yeniden ekleyin.');
      const res = await ep.uploadFile(d.candidateId!, blob, img.name, img.sha256);
      d = { ...d, images: d.images.map((x) => (x.id === img.id ? { ...x, uploadedFileId: res.id } : x)) };
      await store.put(d);
    }
    const c = await ep.getCandidate(d.candidateId!);
    d = applyServerCandidate(d, c);
    await store.put(d);
    return d;
  } catch (e) {
    return fail(d, e);
  }
}

/** Sunucunun OCR önerilerini (gider kalemi dahil), kullanıcının boş bıraktığı alanlara yerleştirir; yazılanı ezmez. */
export function applyServerCandidate(d: Draft, c: Candidate): Draft {
  const f = { ...d.fields };
  const s = c.fields;
  type TextKey = Exclude<keyof DraftFields, 'payment_method'>; // ödeme şekli fişten okunmaz, elle seçilir
  const fill = (k: TextKey, v: string | null | undefined) => { if (!f[k] && v) f[k] = v; };
  if (!f.amount && s.amount) f.amount = s.amount.replace('.', ',');
  fill('document_date', s.document_date);
  fill('document_time', s.document_time && s.time_precision !== 'DATE' ? (s.time_precision === 'MINUTE' ? s.document_time.slice(0, 5) : s.document_time) : '');
  fill('vendor_name', s.vendor_name);
  fill('vendor_tax_no', s.vendor_tax_no);
  fill('document_no', s.document_no);
  fill('category', s.category);
  fill('description', s.description);
  // Okuma bitti ama fişte tarih bulunamadı (el yazısı, silik fiş): fişin yüklendiği gün ve saat yazılır.
  // Kullanıcı fotoğrafa bakıp değiştirebilir.
  if (c.status !== 'OCR_PENDING' && !f.document_date) {
    const at = uploadedAt(c);
    if (at) {
      const tr = isoToTR(at);
      f.document_date = tr.date;
      if (!f.document_time) f.document_time = tr.time;
    }
  }
  const state: LocalState = c.status === 'OCR_PENDING' ? 'OCR_PENDING'
    : c.status === 'REVIEW_REQUIRED' ? 'REVIEW_REQUIRED'
    : c.status === 'SUBMITTED' ? 'POSTED' : 'READY';
  return { ...d, fields: f, candidateVersion: c.version, state, receiptId: c.submitted_receipt_id ?? d.receiptId };
}

/** Fişin sisteme yüklendiği an: ilk fotoğrafın sunucuya ulaştığı zaman. */
export function uploadedAt(c: Candidate | undefined | null): string | null {
  return (c?.files ?? []).filter((x) => x.role === 'ORIGINAL' && x.created_at).map((x) => x.created_at!).sort()[0] ?? null;
}

export function validateFields(f: DraftFields): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!parseUserAmount(f.amount)) errors.amount = 'Tutarı 1.250,50 biçiminde girin.';
  if (!f.document_date) errors.document_date = 'Belge tarihi zorunlu.';
  if (!f.category.trim()) errors.category = 'Kategori zorunlu.';
  if (!f.description.trim()) errors.description = 'Açıklama zorunlu.';
  if (f.document_time && !/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(f.document_time)) errors.document_time = 'Saat SS:DD veya SS:DD:ss olmalı.';
  if (!f.vendor_name?.trim()) errors.vendor_name = 'Satıcı adını fişe bakarak yazın.';
  if (!f.payment_method) errors.payment_method = 'Ödeme şeklini seçin.';
  if (f.payment_method === 'CREDIT_SALE') {
    if (!f.payment_due_date) errors.payment_due_date = 'Veresiye için ödeme tarihini girin.';
    else if (f.document_date && f.payment_due_date < f.document_date) errors.payment_due_date = 'Ödeme tarihi fiş tarihinden önce olamaz.';
  }
  return errors;
}

/** Kullanıcı kontrolünden sonra: alanları gönder ve kesinleştir. */
export async function submitDraft(draft: Draft): Promise<Draft> {
  const store = await draftStore();
  const errors = validateFields(draft.fields);
  if (Object.keys(errors).length) throw new Error(Object.values(errors)[0]);
  let d: Draft = draft;
  if (!d.candidateId || d.images.some((i) => !i.uploadedFileId)) {
    d = await uploadDraft(d);
    if (d.state === 'FAILED' || !d.candidateId) return d;
  }
  d = { ...d, state: 'SUBMITTING', lastError: null };
  await store.put(d);
  try {
    const f = d.fields;
    const time = f.document_time || null;
    const c = await ep.updateCandidate(d.candidateId!, d.candidateVersion ?? 1, {
      amount: parseUserAmount(f.amount), document_date: f.document_date, document_time: time,
      time_precision: time ? (time.length > 5 ? 'SECOND' : 'MINUTE') : 'DATE',
      vendor_name: f.vendor_name || null, vendor_tax_no: f.vendor_tax_no || null, document_no: f.document_no || null,
      category: f.category.trim(), description: f.description.trim(),
      payment_method: f.payment_method || null,
      payment_due_date: f.payment_method === 'CREDIT_SALE' ? f.payment_due_date || null : null,
    });
    d = { ...d, candidateVersion: c.version };
    await store.put(d);
    const receipt = await ep.submitCandidate(d.candidateId!, c.version, d.submitIdempotencyKey);
    d = { ...d, state: 'POSTED', receiptId: receipt.id };
    await store.put(d);
    await store.removeImages(d.id); // sunucuda kesinleşti; yerel kopyaya gerek yok
    return d;
  } catch (e) {
    if (e instanceof ApiError && e.code === 'VERSION_CONFLICT' && d.candidateId) {
      const c = await ep.getCandidate(d.candidateId);
      d = { ...d, candidateVersion: c.version };
    }
    if (e instanceof ApiError && e.code === 'CANDIDATE_ALREADY_SUBMITTED') {
      d = { ...d, state: 'POSTED', receiptId: String(e.details.receipt_id ?? d.receiptId ?? '') };
      await store.put(d);
      return d;
    }
    if (e instanceof ApiError && e.code === 'REVIEW_REQUIRED') {
      d = { ...d, state: 'REVIEW_REQUIRED', lastError: { code: e.code, message: e.message } };
      await store.put(d);
      return d;
    }
    // ağ hatasında submit anahtarı KORUNUR; yeniden deneme aynı fişi döndürür (T08)
    return fail(d, e);
  }
}

async function fail(d: Draft, e: unknown): Promise<Draft> {
  const store = await draftStore();
  const isNet = e instanceof NetworkError;
  const attempts = d.attempts + 1;
  const next: Draft = {
    ...d,
    state: isNet ? (d.candidateId ? 'READY' : 'LOCAL_DRAFT') : 'FAILED',
    lastError: e instanceof ApiError ? { code: e.code, message: e.message }
      : { code: isNet ? 'OFFLINE' : 'ERROR', message: (e as Error).message },
    attempts,
    nextAttemptAt: Date.now() + Math.min(10 * 60_000, 2_000 * 2 ** Math.min(attempts, 8)),
  };
  await store.put(next);
  return next;
}

/** Bağlantı geri geldiğinde yükleme bekleyen taslakları dener (gönderimi kullanıcı yapar). */
export async function retryPendingUploads(userId: string): Promise<void> {
  const store = await draftStore();
  for (const d of await store.list(userId)) {
    const pending = d.images.some((i) => !i.uploadedFileId) || (!d.candidateId && d.images.length > 0);
    if (pending && d.nextAttemptAt <= Date.now() && d.state !== 'POSTED' && d.state !== 'SUBMITTING') {
      await uploadDraft(d);
    }
  }
}

export async function discardDraft(d: Draft): Promise<void> {
  if (d.candidateId && d.state !== 'POSTED') {
    try { await ep.deleteCandidate(d.candidateId); } catch { /* sunucuda yoksa yerel silme yeterli */ }
  }
  await (await draftStore()).remove(d.id);
}

function guessMime(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'png' ? 'image/png' : ext === 'heic' || ext === 'heif' ? 'image/heic' : 'image/jpeg';
}
